import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  MyselfDocument,
  SelfAssessmentSavedAnswersDocument,
  SelfAssessmentStatementsDocument,
} from "@/graphql/generated/operations";
import { elementDescription } from "@/lib/element-descriptions";
import { elementEvaluation } from "@/lib/element-evaluations";
import { buildGrowAssistantContext, growBaseContext } from "@/lib/grow-assistant-context";
import { questionnaireState, weakestStatements } from "@/lib/self-assessment";
import { useAssistantContext, type AssistantContextItem } from "@/lib/use-assistant-context";

/** What a Grow page already knows about the power on screen. The hook adds the person's own data. */
export interface GrowImproveFacts {
  /** The power's key, or null for an unknown power: every query is skipped and nothing is built. */
  element: string | null;
  powerName: string;
  /**
   * The SELF-ASSESSMENT score, never a combined one: the assistant may only speak about the
   * self-assessment (@external-adr AGENT-SERVICE-0018-self-assessment-only-power-access).
   */
  score: number | null;
  answered: number;
  total: number;
  /** genesis' own band wording, the text the page shows beside the score. */
  bandLabel: string | null;
  /**
   * The page's own query for the score and the answered count is in flight, including a refetch:
   * right after a save the cached score is the old one.
   */
  scoreLoading: boolean;
}

/**
 * The improve trigger's data and consent state, shared by the two Grow pages that offer it
 * (`ElementResultPage`, `PowerDetailPage`). The consent rules it implements are
 * @adr SPARK-0058-grow-improve-trigger-asks-before-personal-data-travels@1.
 */
export function useGrowImproveContext(facts: GrowImproveFacts) {
  const intl = useIntl();
  const { element, powerName, score, answered, total, bandLabel, scoreLoading } = facts;

  // `element as never` because the generated variable is `CreativityElement!` and the page holds a
  // plain string; `skip` keeps an unknown power from firing a request with an invalid enum.
  const variables = { element: element as never };
  const statements = useQuery(SelfAssessmentStatementsDocument, {
    variables,
    skip: !element,
    fetchPolicy: "cache-and-network",
  });
  const saved = useQuery(SelfAssessmentSavedAnswersDocument, {
    variables,
    skip: !element,
    fetchPolicy: "cache-and-network",
  });
  // The profile cache is warm on every Grow route (AppShell renders AccountMenu); cache-first keeps
  // a fresh mount from firing a request of its own.
  const myself = useQuery(MyselfDocument, { fetchPolicy: "cache-first" });
  // The trigger waits until everything it would freeze is current (ADR rule 3). The score and the
  // saved answers wait out a refetch as well: they change with every save, and the result page is
  // reached straight from one, so their cached values are the previous state. The statements and the
  // profile do not change that way, so cached ones count. A failed query ends its wait like any
  // other, so an error never leaves the button dead.
  const contextPending =
    scoreLoading ||
    saved.loading ||
    (statements.loading && !statements.data) ||
    (myself.loading && !myself.data);

  const statementList = statements.data?.selfAssessmentStatements;
  const savedAnswers = saved.data?.selfAssessmentSavedAnswers;
  const weak = useMemo(() => weakestStatements(statementList ?? [], savedAnswers), [statementList, savedAnswers]);
  const profile = myself.data?.myself ?? null;
  const growInput = useMemo(
    () =>
      element
        ? {
            powerName,
            elementKey: element,
            definition: elementDescription(element, intl.locale),
            score,
            answered,
            total,
            bandLabel,
            // The banded paragraph for the SELF-ASSESSMENT score. The power page displays the one for
            // the combined score; the improve answer is about the questionnaire, and it introduces
            // this paragraph as the person's self-assessment so the two do not read as rivals.
            assessment: elementEvaluation(element, score, intl.locale),
            weak,
            profile,
            // The answer's fixed texts in the app's language, so the prompt carries no
            // German/English pairs and the headings follow the UI like any other string.
            labels: {
              leadIn: intl.formatMessage({ id: "grow.answer.leadIn" }),
              growthAreas: intl.formatMessage({ id: "grow.answer.growthAreas" }),
              example: intl.formatMessage({ id: "grow.answer.example" }),
              situation: intl.formatMessage({ id: "grow.answer.situation" }),
              approach: intl.formatMessage({ id: "grow.answer.approach" }),
              outcome: intl.formatMessage({ id: "grow.answer.outcome" }),
            },
          }
        : null,
    [element, powerName, intl, score, answered, total, bandLabel, weak, profile],
  );
  const items = useMemo(() => (growInput ? buildGrowAssistantContext(growInput) : null), [growInput]);
  // Sent when nothing personal travels (ADR rule 2): the definition and the questionnaire state.
  const baseItems = useMemo(() => (growInput ? growBaseContext(growInput) : null), [growInput]);

  // The frozen list for ONE power (ADR rules 3 and 4). The route reuses the page component when
  // only the power in the URL changes, so the state records which power it belongs to. A consent
  // for another power is cleared while rendering, React's pattern for state that follows a prop,
  // so no commit ever pairs one power's page with another power's consent; an effect would leave
  // one render with the old list still registered. `consent &&` first: with no consent and no
  // element both sides of `consent?.element === element` are undefined and the comparison is true.
  const [consent, setConsent] = useState<{ element: string; items: AssistantContextItem[] } | null>(null);
  if (consent && consent.element !== element) setConsent(null);
  const consented = consent && consent.element === element ? consent.items : null;
  useAssistantContext(consented);

  return {
    items,
    baseItems,
    contextPending,
    consented: consented != null,
    onConsent: (frozen: AssistantContextItem[]) => {
      if (element) setConsent({ element, items: frozen });
    },
    // The score exists from the first answer on (genesis averages whatever is there), so
    // completeness is the only thing that says whether it may be evaluated.
    qState: questionnaireState(answered, total),
  };
}
